import { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { MessageCircle, X, Send, Minimize2, Maximize2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

interface Message {
  role: 'user' | 'assistant'
  content: string
}

interface ChatWindowProps {
  initialMessage?: string
  position?: 'bottom-right' | 'bottom-left'
  accentColor?: string
  companyName?: string
  agentName?: string
  minimizedText?: string
  publisherId: string
  showIconOnly?: boolean
  useProfileContent?: boolean
  useListings?: boolean
  stream?: boolean
}

// Streaming configuration
const STREAMING_CONFIG = {
  cursorBlinkSpeed: 500, // milliseconds
  updateThrottle: 100,   // milliseconds
  enabled: true,         // default streaming enabled
  fallbackTimeout: 5000  // milliseconds
}

export const ChatWindow = ({
  initialMessage = "Hi! I'm here to help. What brings you here today?",
  position = 'bottom-right',
  accentColor = '#0ea5e9',
  companyName = 'Support',
  agentName = 'AI Assistant',
  minimizedText = 'Chat with us',
  publisherId,
  showIconOnly = true,
  useProfileContent = true,
  useListings = true,
  stream = true
}: ChatWindowProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [message, setMessage] = useState('')
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: initialMessage }
  ])
  const [isLoading, setIsLoading] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const streamingEnabled = stream && STREAMING_CONFIG.enabled

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Handle streaming response
  const handleStreamingResponse = async (response: Response) => {
    const reader = response.body?.getReader()
    const decoder = new TextDecoder()
    
    if (!reader) throw new Error('ReadableStream not supported')
    
    // Create assistant message placeholder
    const assistantMessage: Message = { role: 'assistant', content: '' }
    setMessages(prev => [...prev, assistantMessage])
    setIsLoading(false)
    setIsStreaming(true)
    
    let lastUpdateTime = 0
    
    const updateContent = (content: string) => {
      const now = Date.now()
      if (now - lastUpdateTime >= STREAMING_CONFIG.updateThrottle) {
        setMessages(prev => {
          const newMessages = [...prev]
          newMessages[newMessages.length - 1] = { role: 'assistant', content }
          return newMessages
        })
        lastUpdateTime = now
      }
    }
    
    try {
      let buffer = ''
      
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || '' // Keep incomplete line in buffer
        
        for (const line of lines) {
          if (line.trim() === '') continue
          
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim()
            if (data === '[DONE]') {
              setIsStreaming(false)
              return
            }
            
            try {
              const parsed = JSON.parse(data)
              if (parsed.content) {
                assistantMessage.content += parsed.content
                updateContent(assistantMessage.content)
              }
            } catch (e) {
              console.warn('Failed to parse streaming chunk:', data)
            }
          }
        }
      }
    } catch (streamError) {
      console.error('Streaming error:', streamError)
      assistantMessage.content = "I'm sorry, there was an issue with the response. Please try again."
      updateContent(assistantMessage.content)
    } finally {
      setIsStreaming(false)
      reader.releaseLock()
      
      // Final update
      setMessages(prev => {
        const newMessages = [...prev]
        newMessages[newMessages.length - 1] = { role: 'assistant', content: assistantMessage.content }
        return newMessages
      })
    }
  }

  // Handle regular JSON response
  const handleRegularResponse = async (response: Response) => {
    const responseData = await response.json()
    
    // Handle different response formats
    let content = ''
    if (responseData.data && responseData.data.content) {
      content = responseData.data.content
    } else if (responseData.content) {
      content = responseData.content
    } else if (responseData.message) {
      content = responseData.message
    } else if (typeof responseData === 'string') {
      content = responseData
    } else {
      content = 'Received response but content format is not recognized.'
      console.warn('Unexpected response format:', responseData)
    }
    
    setMessages(prev => [...prev, { 
      role: 'assistant',
      content: content
    }])
  }

  const handleSend = async () => {
    if (!message.trim()) return

    const newMessage = { role: 'user' as const, content: message }
    setMessages(prev => [...prev, newMessage])
    setMessage('')
    setIsLoading(true)

    try {
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/tools/chat?stream=${streamingEnabled}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          messages: [...messages, newMessage],
          publisherId,
          useProfileContent,
          useListings,
          agentName,
          stream: streamingEnabled
        })
      })

      if (!response.ok) {
        throw new Error('Network response was not ok')
      }

      if (streamingEnabled) {
        // Handle streaming response
        await handleStreamingResponse(response)
      } else {
        // Handle regular JSON response
        await handleRegularResponse(response)
      }
    } catch (error) {
      console.error('Chat error:', error)
      setMessages(prev => [...prev, { 
        role: 'assistant',
        content: "I'm sorry, I'm having trouble connecting. Please try again."
      }])
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const renderMessage = (content: string, isUser: boolean, showTypingCursor = false) => {
    if (isUser) {
      return content;
    }
    
    return (
      <div className="relative">
        <ReactMarkdown 
          remarkPlugins={[remarkGfm]}
          className="prose prose-sm dark:prose-invert max-w-none"
          components={{
            // Override default styling for certain elements
            p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
            a: ({ href, children }) => (
              <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                {children}
              </a>
            ),
            ul: ({ children }) => <ul className="list-disc ml-4 mb-1">{children}</ul>,
            ol: ({ children }) => <ol className="list-decimal ml-4 mb-1">{children}</ol>,
            li: ({ children }) => <li className="mb-1">{children}</li>,
            code: ({ children }) => (
              <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded text-sm">
                {children}
              </code>
            ),
            pre: ({ children }) => (
              <pre className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md overflow-x-auto my-2">
                {children}
              </pre>
            ),
          }}
        >
          {content}
        </ReactMarkdown>
        {showTypingCursor && (
          <span 
            className="inline-block w-2 h-4 ml-1 animate-pulse"
            style={{ backgroundColor: accentColor }}
          />
        )}
      </div>
    );
  };

  if (!isOpen) {
    return (
      <div className={cn(
        'fixed z-50',
        position === 'bottom-right' ? 'right-4' : 'left-4',
        'bottom-4'
      )}>
        <Button
          onClick={() => setIsOpen(true)}
          className="rounded-full p-4 shadow-lg"
          style={{ backgroundColor: accentColor }}
        >
          <MessageCircle className="w-6 h-6" />
          {!showIconOnly && <span className="ml-2">{minimizedText}</span>}
        </Button>
      </div>
    )
  }

  return (
    <div className={cn(
      'fixed z-50',
      position === 'bottom-right' ? 'right-4 md:right-4' : 'left-4 md:left-4',
      'bottom-4',
      'w-full md:w-auto max-w-[95vw] md:max-w-none mx-4 md:mx-0'
    )}>
      <Card className="w-full md:w-[380px] shadow-xl">
        {/* Header */}
        <div 
          className="p-3 md:p-4 flex justify-between items-center rounded-t-lg"
          style={{ backgroundColor: accentColor }}
        >
          <div className="flex items-center space-x-2 text-white">
            <MessageCircle className="w-4 h-4 md:w-5 md:h-5" />
            <div className="flex flex-col">
              <span className="font-medium text-sm md:text-base">{companyName}</span>
              <span className="text-xs md:text-sm opacity-90">{agentName}</span>
            </div>
          </div>
          <div className="flex items-center space-x-1 md:space-x-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 md:h-8 md:w-8 text-white hover:text-white/80 active:scale-95 transition-transform"
              onClick={() => setIsMinimized(!isMinimized)}
            >
              {isMinimized ? <Maximize2 className="h-3 w-3 md:h-4 md:w-4" /> : <Minimize2 className="h-3 w-3 md:h-4 md:w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 md:h-8 md:w-8 text-white hover:text-white/80 active:scale-95 transition-transform"
              onClick={() => setIsOpen(false)}
            >
              <X className="h-3 w-3 md:h-4 md:w-4" />
            </Button>
          </div>
        </div>

        {/* Messages */}
        {!isMinimized && (
          <>
            <div className="h-[50vh] md:h-[400px] overflow-y-auto p-3 md:p-4 space-y-3 md:space-y-4 overscroll-contain">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={cn(
                    'flex',
                    msg.role === 'user' ? 'justify-end' : 'justify-start'
                  )}
                >
                  <div
                    className={cn(
                      'max-w-[85%] rounded-lg p-2.5 md:p-3 text-sm md:text-base',
                      msg.role === 'user'
                        ? 'text-white'
                        : 'bg-gray-100 dark:bg-gray-800'
                    )}
                    style={msg.role === 'user' ? { backgroundColor: accentColor } : {}}
                  >
                    {renderMessage(
                      msg.content, 
                      msg.role === 'user', 
                      msg.role === 'assistant' && isStreaming && index === messages.length - 1
                    )}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="max-w-[85%] rounded-lg p-2.5 md:p-3 bg-gray-100 dark:bg-gray-800">
                    <div className="flex space-x-1.5">
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-gray-400 animate-bounce" />
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:0.2s]" />
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:0.4s]" />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 md:p-4 border-t">
              <div className="flex space-x-2">
                <Input
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Type a message..."
                  className="flex-1 text-sm md:text-base h-9 md:h-10"
                />
                <Button 
                  onClick={handleSend}
                  style={{ backgroundColor: accentColor }}
                  className="px-2.5 md:px-3 h-9 md:h-10 active:scale-95 transition-transform"
                >
                  <Send className="h-3.5 w-3.5 md:h-4 md:w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  )
}
